// CompiledQueryCard.tsx — compiled query card with swim lanes (PRI-291)
// Spec §3, §4, §9, §10, §12

import React, { useState, useCallback } from 'react'
import type { CompiledQuery, ParseState, Lane, LaneType, ConnectiveValue } from './compiled-query-types'
import { deriveLanes, deriveConnectives, generatePreviewText, mapLaneTypeToCondition } from './compiled-query-types'
import { SwimLane } from './SwimLane'
import { ConnectiveBadge } from './ConnectiveBadge'
import { trackCompiledQueryEdited } from '../../telemetry/events'

interface CompiledQueryCardProps {
  compiledQuery: CompiledQuery | null
  parseState: ParseState
  parseError?: string | null
  editMode: boolean
  onEditToggle: (open: boolean) => void
  onCompiledQueryChange: (q: CompiledQuery) => void
  onRestore: () => void
  isMobile?: boolean
}

// Pencil icon SVG
function PencilIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M8.5 1.5l2 2-6 6-2.5.5.5-2.5 6-6z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

export function CompiledQueryCard({
  compiledQuery,
  parseState,
  parseError,
  editMode,
  onEditToggle,
  onCompiledQueryChange,
  onRestore,
  isMobile = false,
}: CompiledQueryCardProps) {
  const [lanes, setLanes] = useState<Lane[]>(() =>
    compiledQuery ? deriveLanes(compiledQuery) : []
  )
  const [showAddLanePicker, setShowAddLanePicker] = useState(false)
  const [draggingLaneId, setDraggingLaneId] = useState<string | null>(null)
  const [dragOverLaneId, setDragOverLaneId] = useState<string | null>(null)

  // Re-derive lanes when compiledQuery changes (from outside, e.g. new parse result)
  React.useEffect(() => {
    if (compiledQuery) {
      setLanes(deriveLanes(compiledQuery))
    }
  }, [compiledQuery])

  const connectives: ConnectiveValue[] = compiledQuery
    ? deriveConnectives(lanes, compiledQuery)
    : []

  const previewText = compiledQuery ? generatePreviewText(compiledQuery) : ''

  // ── Lane mutation helpers ──────────────────────────────────────────────────

  const handleTargetChange = useCallback((laneId: string, value: string) => {
    if (!compiledQuery) return
    const updated = lanes.map(l => l.id === laneId ? { ...l, target: value } : l)
    setLanes(updated)
    // Rebuild compiledQuery with updated lane targets
    // Simple approach: update subjects/actions/conditions based on lane types
    const newQuery = rebuildQuery(compiledQuery, updated)
    onCompiledQueryChange(newQuery)
  }, [compiledQuery, lanes, onCompiledQueryChange])

  const handleDeleteLane = useCallback((laneId: string) => {
    if (!compiledQuery) return
    const updated = lanes.filter(l => l.id !== laneId)
    setLanes(updated)
    const newQuery = rebuildQuery(compiledQuery, updated)
    onCompiledQueryChange(newQuery)
    trackCompiledQueryEdited({ action: 'delete_lane', lane_count: updated.length })
  }, [compiledQuery, lanes, onCompiledQueryChange])

  // Reorder lanes via drag-and-drop. Removes the source lane and inserts it
  // before the target (or at the end when dropping below the last lane).
  const handleReorderLanes = useCallback((sourceId: string, targetId: string | null) => {
    if (!compiledQuery || sourceId === targetId) return
    const sourceIndex = lanes.findIndex(l => l.id === sourceId)
    if (sourceIndex < 0) return
    const reordered = [...lanes]
    const [moved] = reordered.splice(sourceIndex, 1)
    if (targetId === null) {
      reordered.push(moved)
    } else {
      const targetIndex = reordered.findIndex(l => l.id === targetId)
      if (targetIndex < 0) {
        reordered.push(moved)
      } else {
        reordered.splice(targetIndex, 0, moved)
      }
    }
    setLanes(reordered)
    const newQuery = rebuildQuery(compiledQuery, reordered)
    onCompiledQueryChange(newQuery)
  }, [compiledQuery, lanes, onCompiledQueryChange])

  const handleAddLane = useCallback((type: LaneType = 'SUBJECT') => {
    if (!compiledQuery) return
    const newLane: Lane = {
      id: `lane-new-${Date.now()}`,
      type,
      target: '',
      negated: false,
      connective: lanes.length > 0 ? 'and' : undefined,
    }
    const updated = [...lanes, newLane]
    setLanes(updated)
    const newQuery = rebuildQuery(compiledQuery, updated)
    onCompiledQueryChange(newQuery)
    setShowAddLanePicker(false)
    trackCompiledQueryEdited({ action: 'add_lane', lane_count: updated.length, lane_type: type })
  }, [compiledQuery, lanes, onCompiledQueryChange])

  // Connective at index N is the gap BEFORE lane N+1. Update only that lane's
  // connective so each gap is independent (spec §7).
  const handleConnectiveChange = useCallback((index: number, value: ConnectiveValue) => {
    if (!compiledQuery) return
    const targetLaneIndex = index + 1
    const updated = lanes.map((l, i) =>
      i === targetLaneIndex
        ? { ...l, connective: value, negated: value === 'and_not' }
        : l
    )
    setLanes(updated)
    const newQuery = rebuildQuery(compiledQuery, updated)
    onCompiledQueryChange(newQuery)
  }, [compiledQuery, lanes, onCompiledQueryChange])

  // ── Render states ──────────────────────────────────────────────────────────

  // IDLE: render nothing (ghost lane is rendered by V2InputBar directly)
  if (parseState === 'idle') return null

  // ERROR state
  if (parseState === 'error') {
    return (
      <div style={{
        margin: '0 0 6px',
        padding: '8px 12px',
        borderTop: '1px solid #D4D0C8',
      }}>
        <div role="alert" style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 13, color: '#DC2626',
          fontFamily: 'Inter, -apple-system, sans-serif',
        }}>
          <span aria-hidden="true">⚠️</span>
          <span>{parseError ?? "Darwin doesn't understand this prompt — try something more specific"}</span>
        </div>
      </div>
    )
  }

  // PARSING state — skeleton lanes
  if (parseState === 'parsing') {
    return (
      <div
        data-testid="compiled-query-card"
        style={{
          margin: '0 0 6px',
          borderTop: '1px solid #D4D0C8',
          padding: '8px 10px',
        }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[0, 1, 2].map(i => (
            <div
              key={i}
              data-testid="skeleton-lane"
              style={{
                height: isMobile ? 40 : 32,
                borderRadius: 4,
                background: '#E8E5DE',
                animation: `pv2-skeleton-pulse 1.2s ease-in-out infinite`,
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </div>
      </div>
    )
  }

  // LANES_VISIBLE or EDIT state
  if (parseState === 'lanes_visible' || parseState === 'edit') {
    return (
      <div
        data-testid="compiled-query-card"
        style={{
          borderTop: '1px solid #D4D0C8',
          animation: 'pv2-card-expand 250ms ease-out',
        }}>
        {/* Card header */}
        <div
          aria-live="polite"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 10px 4px',
            gap: 8,
          }}
        >
          <span style={{
            fontSize: 11,
            color: '#7A7669',
            fontFamily: 'Inter, -apple-system, sans-serif',
            flex: 1,
            minWidth: 0,
          }}>
            Darwin compiles your natural text into this structured query:
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {editMode ? (
              <>
                <button
                  onClick={() => {
                    trackCompiledQueryEdited({ action: 'restore', lane_count: lanes.length })
                    onRestore()
                  }}
                  style={{
                    background: 'none', border: '1px solid #D4D0C8',
                    borderRadius: 4, padding: '2px 7px',
                    fontSize: 11, color: '#7A7669',
                    fontFamily: 'Inter, -apple-system, sans-serif',
                    cursor: 'pointer',
                  }}
                >
                  Restore
                </button>
                <button
                  onClick={() => {
                    trackCompiledQueryEdited({ action: 'edit_toggle_close', lane_count: lanes.length })
                    onEditToggle(false)
                  }}
                  style={{
                    background: '#1B4FE0', border: 'none',
                    borderRadius: 4, padding: '2px 7px',
                    fontSize: 11, color: '#fff',
                    fontFamily: 'Inter, -apple-system, sans-serif',
                    cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  trackCompiledQueryEdited({ action: 'edit_toggle_open', lane_count: lanes.length })
                  onEditToggle(true)
                }}
                aria-label="Edit compiled query"
                title="Edit lanes"
                style={{
                  background: 'none', border: 'none',
                  cursor: 'pointer', color: '#7A7669',
                  padding: 2, display: 'flex', alignItems: 'center',
                  borderRadius: 4,
                }}
              >
                <PencilIcon />
              </button>
            )}
          </div>
        </div>

        {/* Swim lane stack */}
        <div style={{ padding: '0 10px', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {lanes.map((lane, laneIndex) => (
            <React.Fragment key={lane.id}>
              <div
                draggable={editMode}
                data-testid={`lane-wrapper-${lane.id}`}
                onDragStart={(e) => {
                  if (!editMode) return
                  setDraggingLaneId(lane.id)
                  e.dataTransfer.effectAllowed = 'move'
                  // Required for Firefox to fire drag events
                  try { e.dataTransfer.setData('text/plain', lane.id) } catch {}
                }}
                onDragEnd={() => {
                  setDraggingLaneId(null)
                  setDragOverLaneId(null)
                }}
                onDragOver={(e) => {
                  if (!editMode || !draggingLaneId || draggingLaneId === lane.id) return
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  if (dragOverLaneId !== lane.id) setDragOverLaneId(lane.id)
                }}
                onDragLeave={() => {
                  if (dragOverLaneId === lane.id) setDragOverLaneId(null)
                }}
                onDrop={(e) => {
                  if (!editMode || !draggingLaneId) return
                  e.preventDefault()
                  handleReorderLanes(draggingLaneId, lane.id)
                  setDraggingLaneId(null)
                  setDragOverLaneId(null)
                }}
                style={{
                  opacity: draggingLaneId === lane.id ? 0.4 : 1,
                  borderTop: dragOverLaneId === lane.id && draggingLaneId !== lane.id
                    ? '2px solid #1B4FE0'
                    : '2px solid transparent',
                  transition: 'opacity 120ms, border-color 120ms',
                }}
              >
                <SwimLane
                  lane={lane}
                  editMode={editMode}
                  isEmpty={editMode && lane.target.trim() === ''}
                  onTargetChange={v => handleTargetChange(lane.id, v)}
                  onDelete={() => handleDeleteLane(lane.id)}
                  isMobile={isMobile}
                />
              </div>
              {laneIndex < lanes.length - 1 && connectives[laneIndex] !== undefined && (
                <ConnectiveBadge
                  value={connectives[laneIndex]}
                  editMode={editMode}
                  onChange={v => handleConnectiveChange(laneIndex, v)}
                />
              )}
            </React.Fragment>
          ))}

          {/* Add Lane button — edit mode only; opens type picker on click */}
          {editMode && !showAddLanePicker && (
            <button
              onClick={() => setShowAddLanePicker(true)}
              data-testid="add-lane-btn"
              style={{
                marginTop: 6,
                background: 'none',
                border: '1px dashed #D4D0C8',
                borderRadius: 4,
                padding: '4px 10px',
                fontSize: 12,
                color: '#7A7669',
                fontFamily: 'Inter, -apple-system, sans-serif',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
              }}
            >
              + Add lane
            </button>
          )}
          {editMode && showAddLanePicker && (
            <div
              data-testid="add-lane-picker"
              style={{
                marginTop: 6,
                padding: '6px 8px',
                border: '1px dashed #D4D0C8',
                borderRadius: 4,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 4,
                background: '#FAFAF7',
              }}
            >
              <span style={{
                fontSize: 11, color: '#7A7669', marginRight: 4,
                fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.04em',
              }}>
                ADD:
              </span>
              {(['SUBJECT', 'ACTION', 'PRESENCE', 'WHAT TO COUNT', 'THRESHOLD', 'ATTRIBUTE', 'LOCATION', 'STATE'] as LaneType[]).map(t => (
                <button
                  key={t}
                  onClick={() => handleAddLane(t)}
                  data-testid={`add-lane-${t.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid #D4D0C8',
                    borderRadius: 3,
                    padding: '2px 6px',
                    fontSize: 10,
                    fontFamily: 'JetBrains Mono, monospace',
                    letterSpacing: '0.04em',
                    color: '#1A1916',
                    cursor: 'pointer',
                  }}
                >
                  {t}
                </button>
              ))}
              <button
                onClick={() => setShowAddLanePicker(false)}
                aria-label="Cancel add lane"
                style={{
                  marginLeft: 'auto',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#7A7669', fontSize: 14, lineHeight: 1, padding: '0 4px',
                }}
              >
                ×
              </button>
            </div>
          )}
        </div>

        {/* Preview strip */}
        {previewText && (
          <div style={{
            borderTop: '1px solid #D4D0C8',
            padding: '6px 12px',
            fontSize: 12,
            color: '#7A7669',
            fontFamily: 'Inter, -apple-system, sans-serif',
            fontStyle: 'italic',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {previewText}
          </div>
        )}
      </div>
    )
  }

  return null
}

// ── Helper: rebuild CompiledQuery from edited lanes ────────────────────────────

function rebuildQuery(original: CompiledQuery, lanes: Lane[]): CompiledQuery {
  // Always use conditions array when there are 2+ lanes so per-lane connectives are preserved
  if ((original.conditions && original.conditions.length > 0) || lanes.length > 1) {
    const conditions = lanes.map(l => ({
      // PRI-271 fix (bug 1): use full LaneType -> Condition.type mapping so
      // LOCATION/ATTRIBUTE/etc. survive the round-trip through deriveLanes().
      type: mapLaneTypeToCondition(l.type),
      target: l.target,
      negated: l.negated,
      ...(l.connective ? { connective: l.connective } : {}),
    }))
    // PRI-271 fix (bug 3): do NOT sync the global `connective` field from a
    // per-lane edit. Each gap is independent (spec §7). Mutating the global
    // field caused the next deriveLanes() to overwrite every lane's connective.
    return { ...original, conditions }
  }

  // Otherwise update the flat arrays
  const subjects = lanes.filter(l => l.type === 'SUBJECT').map(l => l.target)
  const actions = lanes.filter(l => l.type === 'ACTION').map(l => l.target)
  const absenceTerms = lanes.filter(l => l.type === 'SUBJECT' && l.negated).map(l => l.target)
  const countTerms = lanes.filter(l => l.type === 'WHAT TO COUNT').map(l => l.target)

  return {
    ...original,
    subjects,
    actions,
    absence_terms: absenceTerms,
    count_terms: countTerms,
  }
}
